import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { StepNavigationService } from '../shared/services/step-navigation/step-navigation.service';
import {
    FormControl,
    FormGroup,
    FormsModule,
    ReactiveFormsModule,
    Validators,
} from '@angular/forms';
import { MatSelectModule } from '@angular/material/select';
import { MatInputModule } from '@angular/material/input';
import { MatError, MatFormFieldModule } from '@angular/material/form-field';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatIconModule } from '@angular/material/icon';
import { takeUntil, map, tap, filter } from 'rxjs';
import { ResourcesService } from '../../../core/services/resources/resources.service';
import { ReferenceData } from '../../../core/services/resources/models/reference-data';
import {
    ApplicationPurposeCodeEnum,
    isUnsupportedPurpose,
} from '../shared/constants/application-purpose-codes';
import { DropdownOption } from '../../../core/models/dropdown-option';
import { ApplicationPreparationQuestionnaireService } from '../shared/services/application-preparation-questionnaire/application-preparation-questionnaire.service';
import { FormGroupType } from '../../../core/models/form-group-type';
import { ApplicationCreationRequest } from '../shared/models/requests/application-creation-request';
import { ApplicationPreparationBaseComponent } from '../../../shared/components/application-preparation-base/application-preparation-base.component';
import { RegExPattern } from '../../../core/models/regexp-pattern/regex-pattern';
import { RegexInputDirective } from '../../../shared/directives/regex-input/regex-input.directive';
import {
    PurposeOfFilingErrorMessages,
    PurposeOfFilingValidatorConfig,
} from '../shared/constants/purpose-of-filing-validation-configurations';

import { ApplicationCreationStatus } from '../shared/models/responses/application-creation-response';
import { ApplicationTemplateSelectionComponent } from './components/application-template-selection/application-template-selection.component';
import { ApplicationTemplateService } from '../shared/services/application-template/application-template.service';
import { GridApplicationTemplateRowResponse } from '../template/components/application-template-dashboard/models/grid-application-template-row-response';
import { ModalComponent } from '../../../shared/components/modal/modal.component';
import { NavigationStart, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { RouteConstants } from '../../../core/constants/routes';
import { LargeApplicationNotificationMessages } from './constants/purpose-of-filling-messages';
import { LoadingService } from '../../../core/services/loading/loading.service';
import { HtmlInputConstants } from '../../../core/constants/html-input-constants';
import { IMaskConstants } from '../../../core/constants/imask';
import { IMaskDirective } from 'angular-imask';
import { AppStorageService } from '../../../core/services/app-storage/app-storage.service';

@Component({
    selector: 'app-purpose-of-filing',
    templateUrl: './purpose-of-filing.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        MatSelectModule,
        MatFormFieldModule,
        MatInputModule,
        FormsModule,
        ReactiveFormsModule,
        MatButtonModule,
        MatError,
        MatTooltipModule,
        MatIconModule,
        RegexInputDirective,
        ApplicationTemplateSelectionComponent,
        IMaskDirective,
    ],
})
export class PurposeOfFilingComponent
    extends ApplicationPreparationBaseComponent
    implements OnInit
{
    override form: FormGroup<
        Partial<FormGroupType<ApplicationCreationRequest>>
    > = new FormGroup<Partial<FormGroupType<ApplicationCreationRequest>>>({
        applicationPurpose: new FormControl(null, Validators.required),
        callSign: new FormControl<string | null>(
            null,
            PurposeOfFilingValidatorConfig.callSign.default,
        ),
        templateApplicationMasterId: new FormControl<number | null>(null),
    });

    /**
     * Future steps are needed in the following scenarios:
     *
     * - When a call sign is entered, this results in creating an application from existing license data.
     * - When a template is selected, this results in creating an application from a template.
     */
    override shouldSubmit(): boolean {
        const purpose = this.form.controls.applicationPurpose?.value;
        return Boolean(
            isUnsupportedPurpose(purpose) ||
                this.form.controls.callSign?.value ||
                this.form.controls.templateApplicationMasterId?.value,
        );
    }

    /**
     * When creating an application from an existing license, it may take time
     * so there should be a timeout.
     */
    override shouldAddTimeout(): boolean {
        return Boolean(this.form.controls.callSign?.value);
    }

    applicationPurposes: DropdownOption[] = [];
    applicationTemplatesAvailableForSelection: GridApplicationTemplateRowResponse[] =
        [];

    readonly regExPattern = RegExPattern;
    readonly htmlInputConstants = HtmlInputConstants;
    readonly imask = IMaskConstants;

    constructor(
        protected override readonly stepService: StepNavigationService,
        protected override readonly applicationPreparationQuestionnaireService: ApplicationPreparationQuestionnaireService,
        protected override readonly appStorageService: AppStorageService,
        private readonly applicationTemplateService: ApplicationTemplateService,
        private readonly resourceService: ResourcesService,
        private readonly router: Router,
        private readonly dialog: MatDialog,
        private readonly loadingService: LoadingService,
    ) {
        super(
            stepService,
            applicationPreparationQuestionnaireService,
            appStorageService,
        );
    }

    override ngOnInit(): void {
        super.ngOnInit();

        this.setApplicationPurposes();
        this.setupFormValidators();
        this.setupFormListeners();

        // Hide spinner if user navigates away from this component ONLY
        // because creating an application may take time.
        this.router.events
            .pipe(
                takeUntil(this.destroy$),
                filter((event) => event instanceof NavigationStart),
                tap(() => {
                    this.loadingService.hide();
                }),
            )
            .subscribe();
    }

    showCallSignSearch(): boolean {
        return this.callSignFieldIsVisible(this.form.value.applicationPurpose);
    }

    getCallSignError(): string | undefined {
        const control = this.form.controls.callSign;
        if (!control?.errors || (!control.touched && !control.dirty)) {
            return undefined;
        }

        const firstErrorKey = Object.keys(control.errors)[0];
        return PurposeOfFilingErrorMessages.callSign[
            firstErrorKey as keyof typeof PurposeOfFilingErrorMessages.callSign
        ];
    }

    /**
     * Method returns whether the application template selection grid should be displayed.
     *
     * The conditions for displaying the grid are:
     * - The application purpose is new
     * - There are application templates available for selection
     */
    showApplicationTemplateSelectionGrid(): boolean {
        return (
            this.form.controls.applicationPurpose?.value ===
                ApplicationPurposeCodeEnum.new &&
            !!this.applicationTemplatesAvailableForSelection.length
        );
    }

    protected override handleFormSubmission(
        status: ApplicationCreationStatus,
    ): void {
        switch (status) {
            case ApplicationCreationStatus.CallSignNotFound:
                this.handleCallSignError('notFound');
                break;
            case ApplicationCreationStatus.Timeout:
                this.stopSpinner();
                this.displayLargeApplicationNotificationDialog();
                break;
        }
    }

    private handleCallSignError(
        errorKey: keyof typeof PurposeOfFilingErrorMessages.callSign,
    ): void {
        const message = PurposeOfFilingErrorMessages.callSign[errorKey];
        this.form.controls.callSign?.setErrors({ [errorKey]: message });
    }

    private stopSpinner(): void {
        this.loadingService.hide();
    }

    private displayLargeApplicationNotificationDialog(): void {
        this.dialog.open(ModalComponent, {
            disableClose: true,
            data: {
                title: LargeApplicationNotificationMessages.ModalTitle,
                content: LargeApplicationNotificationMessages.ModalContent,
                primaryAction: {
                    title: LargeApplicationNotificationMessages.ModalButtonTitle,
                    action: () =>
                        this.router.navigate([
                            RouteConstants.Dashboard.getFullRoute('INDEX'),
                        ]),
                },
                isShowCancel: false,
            },
        });
    }

    private setupFormValidators() {
        this.form.controls.applicationPurpose?.valueChanges
            .pipe(
                takeUntil(this.destroy$),
                tap((value) => this.updateCallSignInputValidator(value)),
            )
            .subscribe();
    }

    private setupFormListeners() {
        this.form.controls.applicationPurpose?.valueChanges
            .pipe(
                takeUntil(this.destroy$),
                tap((applicationPurpose: ApplicationPurposeCodeEnum | null) => {
                    this.handleApplicationPurposeChange(applicationPurpose);
                }),
            )
            .subscribe();
    }

    private handleApplicationPurposeChange(
        applicationPurpose: ApplicationPurposeCodeEnum | null,
    ): void {
        if (
            applicationPurpose === ApplicationPurposeCodeEnum.new &&
            this.applicationTemplatesAvailableForSelection.length === 0
        ) {
            this.loadApplicationTemplatesForSelection();
        } else {
            this.form.controls.templateApplicationMasterId?.reset();
        }
    }

    private updateCallSignInputValidator(
        applicationPurpose: ApplicationPurposeCodeEnum | null,
    ): void {
        const callSignControl = this.form.controls.callSign!;

        if (this.callSignFieldIsVisible(applicationPurpose)) {
            callSignControl.setValidators(
                PurposeOfFilingValidatorConfig.callSign.required,
            );
        } else {
            callSignControl.clearValidators();
            callSignControl.reset();
        }

        callSignControl.updateValueAndValidity();
    }

    /**
     * An entered call sign field results in retreiving an existing license as a starting
     * point for an application. As a result, the call sign field is only visible for
     * non-new supported application types.
     */
    private callSignFieldIsVisible(
        applicationPurpose: ApplicationPurposeCodeEnum | null | undefined,
    ): boolean {
        return Boolean(
            applicationPurpose &&
                applicationPurpose !== ApplicationPurposeCodeEnum.new &&
                applicationPurpose !== ApplicationPurposeCodeEnum.other &&
                !isUnsupportedPurpose(applicationPurpose),
        );
    }

    private setApplicationPurposes(): void {
        this.resourceService
            .getApplicationPurposes()
            .pipe(
                takeUntil(this.destroy$),
                map((data: ReferenceData[]) =>
                    data.map((item) => this.mapToDropdownOption(item)),
                ),
            )
            .subscribe((dropdownOptions: DropdownOption[]) => {
                this.applicationPurposes = dropdownOptions;
            });
    }

    private loadApplicationTemplatesForSelection(): void {
        this.applicationTemplateService
            .getAll(ApplicationPurposeCodeEnum.new)
            .pipe(
                takeUntil(this.destroy$),
                tap(
                    (
                        applicationTemplatesAvailableForSelection: GridApplicationTemplateRowResponse[],
                    ) => {
                        this.applicationTemplatesAvailableForSelection =
                            applicationTemplatesAvailableForSelection;
                    },
                ),
            )
            .subscribe();
    }

    private mapToDropdownOption(item: ReferenceData): DropdownOption {
        return {
            value: item.code,
            label:
                item.code === ApplicationPurposeCodeEnum.other
                    ? item.description
                    : `${item.code} - ${item.description}`,
        };
    }
}
